import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api, ApiError } from '@/api/client'
import { useT } from '@/i18n'
import { commonMessages } from '@/i18n/messages/common'
import { usersMessages } from '@/i18n/messages/users'
import { useWfT } from '@/features/workflow/shared'
import { Select } from '@/components/Select'
import { ConfirmButton, Credentials, ErrorLine, Modal, btnPrimary, btnSecondary, inputCls, labelCls } from './parts'
import { fullName, loginTypeLabel, roleName, type AccessRole, type AuOptions, type DirectoryUser } from './types'

/** `label` is a key into usersMessages — translated at render time. */
export const PAGES: { key: string; label: keyof typeof usersMessages.en }[] = [
  { key: 'dashboard', label: 'pageDashboard' },
  { key: 'rtk', label: 'pageRtk' },
  { key: 'aufmass', label: 'pageAufmass' },
  { key: 'projects', label: 'pageProjects' },
  { key: 'bonus', label: 'pageBonus' },
  { key: 'users', label: 'pageUsers' },
  { key: 'inventory', label: 'pageInventory' },
  { key: 'inventory_move', label: 'pageInventoryMove' },
  { key: 'fleet', label: 'pageFleet' },
  { key: 'fleet_move', label: 'pageFleetMove' },
  { key: 'billing', label: 'pageBilling' },
  { key: 'billing_manage', label: 'pageBillingManage' },
  { key: 'people', label: 'pagePeople' },
]

const errText = (e: unknown, fallback: string) => (e instanceof ApiError ? e.message : fallback)

/**
 * A person's access — roles, extra pages, admin — and, when the server can write AU logins, their
 * AU account: a new password, switching it off or on, correcting the details AU holds.
 */
export function EditUserDialog({ user, roles: catalog, au, meIsAdmin, meId, onClose }: {
  user: DirectoryUser
  roles: AccessRole[]
  au: AuOptions | undefined
  meIsAdmin: boolean
  meId: string | undefined
  onClose: () => void
}) {
  const qc = useQueryClient()
  const t = useT(usersMessages)
  const tw = useWfT()
  const tc = useT(commonMessages)
  const [roles, setRoles] = useState<Set<string>>(new Set(user.roles))
  const [isAdmin, setIsAdmin] = useState(user.isAdmin)
  const [canEdit, setCanEdit] = useState(user.canEdit)
  const [pages, setPages] = useState<Set<string>>(new Set(user.pages))
  const [error, setError] = useState<string | null>(null)
  const toggleIn = <T,>(set: React.Dispatch<React.SetStateAction<Set<T>>>, v: T) => set((prev) => {
    const next = new Set(prev)
    if (next.has(v)) next.delete(v); else next.add(v)
    return next
  })

  const held = catalog.filter((r) => roles.has(r.role))
  const fromRoles = new Set(held.flatMap((r) => r.pages))
  const roleEdits = held.some((r) => r.canEdit)
  const suggestion = user.suggestedRoles.filter((r) => !roles.has(r))
  const lockedAdmin = !meIsAdmin   // only an admin grants or takes away admin
  const editable = !user.denied && user.active && (meIsAdmin || !user.isAdmin)

  const save = useMutation({
    mutationFn: () => api.put(`/api/users/${user.userId}`, { pages: [...pages], isAdmin, canEdit, roles: [...roles] }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['users'] }); void qc.invalidateQueries({ queryKey: ['user-roles'] }); onClose() },
    onError: (e) => setError(errText(e, t('couldNotSave'))),
  })

  return (
    <Modal onClose={onClose} wide
      title={fullName(user) || t('unknown')}
      subtitle={<>
        {user.username}{user.email ? ` · ${user.email}` : ''}
        <span className="block text-xs">{t('colAuType')}: {loginTypeLabel(user.loginType)}{user.company ? ` · ${user.company}` : ''}</span>
      </>}
      footer={<>
        <ErrorLine error={error} />
        <div className="flex gap-2">
          <button onClick={onClose} className={btnSecondary}>{tc('cancel')}</button>
          <button onClick={() => { setError(null); save.mutate() }} disabled={save.isPending || !editable} className={btnPrimary}>
            {save.isPending ? t('saving') : tc('save')}
          </button>
        </div>
      </>}>

      {!user.active && <p className="rounded-lg bg-neutral-100 px-3 py-2 text-sm text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300">{t('offHint')}</p>}
      {user.denied && <p className="rounded-lg bg-neutral-100 px-3 py-2 text-sm text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300">{t('deniedHint')}</p>}
      {!meIsAdmin && user.isAdmin && <p className="rounded-lg bg-neutral-100 px-3 py-2 text-sm text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300">{t('adminOnlyAdmin')}</p>}

      <fieldset disabled={!editable} className="space-y-4 disabled:opacity-60">
        <label className={`flex items-start gap-2 rounded-lg bg-neutral-50 p-3 text-sm dark:bg-neutral-800/50 ${lockedAdmin ? 'opacity-60' : ''}`}>
          <input type="checkbox" checked={isAdmin} disabled={lockedAdmin} onChange={(e) => setIsAdmin(e.target.checked)}
                 className="mt-0.5 rounded border-neutral-300 text-brand-600" />
          <span>
            <span className="font-medium">{t('fullAdmin')}</span>
            <span className="block text-xs text-neutral-500">{t('fullAdminHint')}</span>
          </span>
        </label>

        <div>
          <span className={labelCls}>{t('rolesTitle')}</span>
          <p className="mt-0.5 text-xs text-neutral-500">{t('rolesHint')}</p>
          {suggestion.length > 0 && (
            <p className="mt-2 flex flex-wrap items-center gap-2 rounded-lg bg-sky-50 px-2.5 py-1.5 text-xs text-sky-900 dark:bg-sky-500/10 dark:text-sky-200">
              {t('suggestedRole', { roles: suggestion.map((r) => roleName(tw, r, catalog)).join(', ') })}
              <button type="button" onClick={() => setRoles((prev) => new Set([...prev, ...suggestion]))} className="rounded-md px-2 py-0.5 font-semibold ring-1 ring-sky-300 hover:bg-sky-100 dark:ring-sky-500/40 dark:hover:bg-sky-500/20">{t('useSuggestion')}</button>
            </p>
          )}
          <div className="mt-2 flex flex-wrap gap-1.5">
            {catalog.map((r) => (
              <button key={r.role} type="button" aria-pressed={roles.has(r.role)} onClick={() => toggleIn(setRoles, r.role)}
                title={r.pages.length ? t('roleOpens', { pages: r.pages.map((p) => t(PAGES.find((x) => x.key === p)?.label ?? 'unknown')).join(', ') }) : t('noModules')}
                className={`rounded-full px-2.5 py-1 text-xs font-medium ring-1 ${roles.has(r.role) ? 'bg-brand-600 text-white ring-brand-600' : 'text-neutral-700 ring-neutral-300 hover:bg-neutral-50 dark:text-neutral-200 dark:ring-neutral-700 dark:hover:bg-neutral-800'}`}>
                {roleName(tw, r.role, catalog)}
              </button>
            ))}
          </div>
        </div>

        <div>
          <span className={labelCls}>{t('modulesTitle')}</span>
          <p className="mt-0.5 text-xs text-neutral-500">{t('modulesHint')}</p>
          <div className={`mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2 ${isAdmin ? 'pointer-events-none opacity-40' : ''}`}>
            {PAGES.map((p) => {
              const viaRole = fromRoles.has(p.key)
              return (
                <label key={p.key} className={`flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm ring-1 ${viaRole ? 'bg-brand-50/60 ring-brand-200 dark:bg-brand-500/10 dark:ring-brand-500/30' : 'ring-neutral-200 dark:ring-neutral-700'}`}>
                  <input type="checkbox" checked={isAdmin || viaRole || pages.has(p.key)} disabled={isAdmin || viaRole}
                         onChange={() => toggleIn(setPages, p.key)} className="rounded border-neutral-300 text-brand-600" />
                  <span className="min-w-0 flex-1">{t(p.label)}</span>
                  {viaRole && !isAdmin && <span className="shrink-0 text-[10px] font-semibold uppercase tracking-wide text-brand-700 dark:text-brand-300">{t('fromRole')}</span>}
                </label>
              )
            })}
          </div>
          {isAdmin && <p className="mt-1 text-xs text-neutral-400">{t('adminHasAllPages')}</p>}
        </div>

        <label className="flex items-start gap-2 text-sm">
          <input type="checkbox" checked={isAdmin || roleEdits || canEdit} disabled={isAdmin || roleEdits} onChange={(e) => setCanEdit(e.target.checked)}
                 className="mt-0.5 rounded border-neutral-300 text-brand-600" />
          <span>
            <span className="font-medium">{t('colCanEdit')}</span>
            <span className="block text-xs text-neutral-500">{roleEdits && !isAdmin ? t('canEditFromRole') : t('canEditHint')}</span>
          </span>
        </label>
      </fieldset>

      <AuAccountSection user={user} au={au} meIsAdmin={meIsAdmin} meId={meId} />
    </Modal>
  )
}

/** The AU login itself: only when the server holds the narrow accounts connection. */
function AuAccountSection({ user, au, meIsAdmin, meId }: {
  user: DirectoryUser; au: AuOptions | undefined; meIsAdmin: boolean; meId: string | undefined
}) {
  const qc = useQueryClient()
  const t = useT(usersMessages)
  const tc = useT(commonMessages)
  const [password, setPassword] = useState<string | null>(null)
  const [editing, setEditing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const name = fullName(user)
  const refresh = () => void qc.invalidateQueries({ queryKey: ['users'] })

  const reset = useMutation({
    mutationFn: () => api.post<{ password: string }>(`/api/users/${user.userId}/au-password`),
    onSuccess: (r) => { setPassword(r.password); setError(null) },
    onError: (e) => setError(errText(e, t('couldNotSave'))),
  })
  const active = useMutation({
    mutationFn: (on: boolean) => api.post(`/api/users/${user.userId}/au-active`, { active: on }),
    onSuccess: () => { refresh(); setError(null) },
    onError: (e) => setError(errText(e, t('couldNotSave'))),
  })

  if (!au?.enabled)
    return (
      <section className="border-t border-neutral-200 pt-4 dark:border-neutral-800">
        <span className={labelCls}>{t('auAccount')}</span>
        <p className="mt-1 text-xs text-neutral-500">{t('auOff')}</p>
      </section>
    )

  const guarded = !meIsAdmin && (user.isAdmin || au.privileged.includes(user.loginType))
  const self = meId === user.userId
  return (
    <section className="space-y-3 border-t border-neutral-200 pt-4 dark:border-neutral-800">
      <div>
        <span className={labelCls}>{t('auAccount')}</span>
        <p className="mt-0.5 text-xs text-neutral-500">{guarded ? t('auAdminOnly') : t('auAccountHint')}</p>
      </div>
      {password && <Credentials username={user.username ?? undefined} password={password} />}
      {editing
        ? <EditAuDetails user={user} au={au} onDone={() => { setEditing(false); refresh() }} />
        : !guarded && (
          <div className="flex flex-wrap gap-2">
            {user.active && <ConfirmButton label={t('resetPassword')} question={t('resetConfirm', { name })} busy={reset.isPending} onConfirm={() => reset.mutate()} />}
            {user.active && !self && <ConfirmButton danger label={t('switchOff')} question={t('switchOffConfirm', { name })} busy={active.isPending} onConfirm={() => active.mutate(false)} />}
            {!user.active && <button type="button" onClick={() => active.mutate(true)} disabled={active.isPending} className={btnSecondary}>{t('switchOn')}</button>}
            {!user.denied && <button type="button" onClick={() => setEditing(true)} className={btnSecondary}>{t('editDetails')}</button>}
          </div>
        )}
      <ErrorLine error={error} />
      {editing && <button type="button" onClick={() => setEditing(false)} className="text-xs text-neutral-500 hover:underline">{tc('cancel')}</button>}
    </section>
  )
}

function EditAuDetails({ user, au, onDone }: { user: DirectoryUser; au: AuOptions; onDone: () => void }) {
  const t = useT(usersMessages)
  const tc = useT(commonMessages)
  const [form, setForm] = useState({
    name: user.name ?? '', surname: user.surname ?? '', email: user.email ?? '',
    loginType: user.loginType, companyId: user.companyId ?? '',
  })
  const [error, setError] = useState<string | null>(null)
  const types = au.loginTypes.includes(user.loginType) ? au.loginTypes : [user.loginType, ...au.loginTypes]
  const save = useMutation({
    mutationFn: () => api.put(`/api/users/${user.userId}/au-account`, {
      name: form.name, surname: form.surname, email: form.email || null, loginType: form.loginType,
      companyId: form.companyId || null, employeeId: user.employeeId,
    }),
    onSuccess: onDone,
    onError: (e) => setError(errText(e, t('couldNotSave'))),
  })
  return (
    <div className="space-y-3 rounded-lg bg-neutral-50 p-3 dark:bg-neutral-800/50">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label={t('firstName')}><input className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
        <Field label={t('lastName')}><input className={inputCls} value={form.surname} onChange={(e) => setForm({ ...form, surname: e.target.value })} /></Field>
        <Field label={t('email')}><input className={inputCls} type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
        <Field label={t('auType')}>
          <Select value={String(form.loginType)} onChange={(v) => setForm({ ...form, loginType: Number(v) })}
            options={types.map((lt) => ({ value: String(lt), label: loginTypeLabel(lt) }))} />
        </Field>
        <Field label={t('company')}>
          <Select value={form.companyId} onChange={(v) => setForm({ ...form, companyId: v })}
            empty={{ value: '', label: t('none') }}
            options={au.companies.map((c) => ({ value: c.id, label: c.name }))} />
        </Field>
      </div>
      <div className="flex items-center justify-between gap-2">
        <ErrorLine error={error} />
        <button type="button" onClick={() => save.mutate()} disabled={save.isPending} className={btnPrimary}>{save.isPending ? t('saving') : tc('save')}</button>
      </div>
    </div>
  )
}

export function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <label className="block space-y-1">
      <span className={labelCls}>{label}</span>
      {children}
      {hint && <span className="block text-xs text-neutral-500">{hint}</span>}
    </label>
  )
}
